import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';

export interface SlotCanvasLayoutProps {
  title: string;
  subtitle?: string;
  /**
   * Optional action cluster rendered in the header row, right-aligned.
   * Use for slot-specific actions (Schedule report, Save, …). Space is tight
   * at narrow canvas widths — prefer a single primary button plus a
   * "more actions" dropdown over three full-width buttons.
   */
  headerActions?: React.ReactNode;
  /**
   * Overrides the body's default `overflow-auto`. Slots whose content owns
   * its own scrolling (iframes, code viewers) pass `overflow-hidden` so the
   * outer body doesn't double-scroll.
   */
  bodyClassName?: string;
  children: React.ReactNode;
}

/**
 * Shared layout for slot bodies rendered inside the canvas frame.
 *
 * Layout:
 *   Header: 52px strip (matches the canvas frame's close-button row), title
 *           (left) + subtitle (smaller, below) + optional headerActions.
 *           pr-24 reserves space for the canvas frame's absolute chrome cluster
 *           (maximize + one of collapse/close) so headerActions never sit under it.
 *   Body:   scrollable by default; see {@link SlotCanvasLayoutProps.bodyClassName}.
 */
export function SlotCanvasLayout({
  title,
  subtitle,
  headerActions,
  bodyClassName,
  children,
}: SlotCanvasLayoutProps) {
  const returnSlotId = useSlotCanvasStore((s) => s.returnSlotId);
  const goBack = useSlotCanvasStore((s) => s.goBack);

  return (
    <div className="flex flex-col h-full w-full">
      {/* pr-24 reserves space for the canvas frame's absolute chrome cluster.
          Maximize + one of collapse/close (mutually exclusive) is ~72px wide,
          so slot headerActions (e.g. Send to Slack) don't overlap it. */}
      <div className="flex items-center gap-2 pl-4 pr-24 h-[52px] border-b border-border bg-white shrink-0">
        {returnSlotId && (
          <button
            type="button"
            onClick={() => goBack()}
            className="-ml-1 shrink-0 p-1 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Back"
            title="Back"
          >
            <ChevronLeft className="size-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground tracking-[-0.28px]">
            {title}
          </div>
          {subtitle && (
            <div className="truncate text-[11px] text-muted-foreground">
              {subtitle}
            </div>
          )}
        </div>
        {headerActions && (
          <div className="flex items-center gap-1 shrink-0">
            {headerActions}
          </div>
        )}
      </div>
      <div className={`flex-1 min-h-0 ${bodyClassName ?? 'overflow-auto'}`}>{children}</div>
    </div>
  );
}
